import {
  EMPLOYMENT_LABELS,
  SENIORITY_LABELS,
  fmtDate,
  regionShortLabel,
} from '@remote-medium/shared';
import { cn } from '@remote-medium/ui';
import { ArrowRight, LoaderCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';

import { RoleStatusChip } from '../../../lib/status-chip.tsx';
import { CARD, H2, LinkButton, ROW_LINK } from '../../../lib/ui.tsx';
import type { CompanyRole } from './context.ts';

const NO_MATCHES = { considered: 0, confirmed: 0, awaitingConsent: 0, saidYes: 0, introduced: 0 };

function RoleItem({ role, matches }: CompanyRole) {
  const p = matches ?? NO_MATCHES;
  const meta = [
    role.seniority && SENIORITY_LABELS[role.seniority],
    role.employmentType && EMPLOYMENT_LABELS[role.employmentType],
    role.regions.length ? role.regions.map(regionShortLabel).join(', ') : 'Worldwide',
    `Posted ${fmtDate(role.createdAt)}`,
  ];
  const stages = [
    p.confirmed && `${p.confirmed} confirmed`,
    p.awaitingConsent && `${p.awaitingConsent} awaiting consent`,
    p.saidYes && `${p.saidYes} said yes`,
    p.introduced && `${p.introduced} introduced`,
  ].filter(Boolean);
  return (
    <li className="flex flex-col gap-4 border-t border-border px-5 py-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-0.5">
          <Link to={`/roles/${role.id}`} className={ROW_LINK}>
            {role.title}
          </Link>
          <span className="text-sm text-muted-foreground">{meta.filter(Boolean).join(' · ')}</span>
        </div>
        <RoleStatusChip status={role.status} />
      </div>
      {role.summary && <p className="m-0 max-w-prose text-sm text-pretty">{role.summary}</p>}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
          {role.status === 'draft' ? (
            <span>Draft, not ranked yet</span>
          ) : (
            <span>
              <span className="font-medium text-foreground tabular-nums">{p.considered}</span>{' '}
              {p.considered === 1 ? 'candidate' : 'candidates'} being considered
            </span>
          )}
          {stages.length > 0 && <span>{stages.join(' · ')}</span>}
        </div>
        <LinkButton to={`/roles/${role.id}`}>
          View role
          <ArrowRight data-icon="inline-end" />
        </LinkButton>
      </div>
    </li>
  );
}

export function CompanyRoles({
  roles,
  loading,
  canPost,
  action,
}: {
  roles: CompanyRole[];
  loading: boolean;
  canPost: boolean;
  action: ReactNode;
}) {
  const open = roles.filter((r) => r.role.status !== 'closed' && r.role.status !== 'draft').length;
  return (
    <section className={cn(CARD, 'flex flex-col')}>
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
        <div className="flex items-baseline gap-3">
          <h2 className={H2}>Roles</h2>
          {roles.length > 0 && <span className="text-xs text-muted-foreground">{open} open</span>}
        </div>
        {action}
      </div>
      {loading ? (
        <p
          role="status"
          className="m-0 flex items-center gap-2 border-t border-border px-5 py-6 text-sm text-muted-foreground"
        >
          <LoaderCircle aria-hidden="true" className="size-4 motion-safe:animate-spin" />
          Loading roles…
        </p>
      ) : roles.length === 0 ? (
        <p className="m-0 border-t border-border px-5 py-6 text-sm text-muted-foreground">
          {canPost
            ? 'No roles posted yet.'
            : 'No roles yet. Roles can be posted once the company is active.'}
        </p>
      ) : (
        <ul className="m-0 flex list-none flex-col p-0">
          {roles.map((r) => (
            <RoleItem key={r.role.id} {...r} />
          ))}
        </ul>
      )}
    </section>
  );
}
